import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';
import { cartNeedsRestoration, restoreCartState } from '@theme/cart-synchronization';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;
  /** @type {Element | null} */
  #summary = null;
  #stickyFrame = 0;
  #closeRevision = 0;
  #resizeObserver = new ResizeObserver(() => this.#scheduleStickyState());

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.addEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);
    if (this.#dialog) this.#resizeObserver.observe(this.#dialog);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.removeEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);
    this.#resizeObserver.disconnect();
    this.#summary = null;
    cancelAnimationFrame(this.#stickyFrame);
    this.#stickyFrame = 0;
  }

  updatedCallback() {
    super.updatedCallback();
    this.#scheduleStickyState();
  }

  #scheduleStickyState() {
    if (this.isConnected && !this.#stickyFrame && this.#themeDrawer?.isOpen) {
      this.#stickyFrame = requestAnimationFrame(() => {
        this.#stickyFrame = 0;
        this.#updateStickyState();
      });
    }
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
    if (cartNeedsRestoration()) restoreCartState().catch(() => {});

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  #handleDrawerClose = (event) => {
    if (event.target === this.#themeDrawer) this.#closeRevision++;
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.detail?.openCart !== false && event.action === 'add' && !this.#themeDrawer?.isOpen;
    // A native Close during this request/render must win over its late completion.
    const closeRevision = this.#closeRevision;
    const focused = document.activeElement;
    const focusWasInside = this.#dialog?.contains(focused);

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    event.promise
      ?.then(async ({ cart, detail, userErrors }) => {
        if (!this.isConnected) return;
        const items = /** @type {import('./component-cart-items').CartItemsComponent | null} */ (this.querySelector('cart-items-component'));
        if (items) {
          await customElements.whenDefined('cart-items-component');
          await items.whenCartRendered(event);
        }
        if (!this.isConnected) return;
        const settle = () => requestAnimationFrame(() => {
          this.#updateStickyState();
          // Confirmed removals can replace the focused row/header while the drawer stays open.
          if (this.isConnected && this.#themeDrawer?.isOpen && focusWasInside && focused && !focused.isConnected && document.activeElement === document.body) {
            this.querySelector('.theme-drawer__close-button')?.focus({ preventScroll: true });
          }
        });

        if (!shouldAutoOpen || closeRevision !== this.#closeRevision || !this.hasAttribute('auto-open') || detail?.didError || userErrors?.length || !(cart?.totalQuantity > 0) || detail?.failedSections?.includes('cart-drawer-section')) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.isConnected || closeRevision !== this.#closeRevision || !this.hasAttribute('auto-open')) return;
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!this.isConnected || !dialog || !this.#themeDrawer?.isOpen) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (summary !== this.#summary) {
      if (this.#summary) this.#resizeObserver.unobserve(this.#summary);
      this.#summary = summary;
      if (summary) this.#resizeObserver.observe(summary);
    }

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const style = getComputedStyle(dialog);
    const drawerHeight = dialog.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
    const summaryHeight = summary.getBoundingClientRect().height;
    const headerHeight = dialog.querySelector('.theme-drawer__header')?.getBoundingClientRect().height || 0;
    const ratio = summaryHeight / drawerHeight;
    // Reserve a usable product viewport instead of letting the dock crowd out its controls.
    const sticky = ratio <= this.#summaryThreshold && drawerHeight - headerHeight - summaryHeight >= 128;
    const value = String(sticky);
    if (dialog.getAttribute('cart-summary-sticky') !== value) dialog.setAttribute('cart-summary-sticky', value);
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
